/**
 * Segnalazioni — raccolta contesto client (spec §5.1).
 *
 * Tutto best-effort: nessun campo è obbligatorio e nessun errore blocca la
 * raccolta. Regola house "no errori silenziosi": ogni catch logga
 * console.warn/console.error.
 *
 * Chiavi storage scoperte sul campo:
 * - `techyweb_tl4k_session` — SessionManager TechyWeb persiste
 *   `{ session, participant: { pseudonymIdentityId, avatar {...} }, userRole }`
 *   (il join sessione di JoinSessionModal/StudentSessionJoin passa da qui).
 * - `tl4k_current_session_pin` / `tl4k-student-pin` — PIN sessione correnti.
 *
 * Daemon techy-client (kiosk-daemon): LocalAPIHandler su 127.0.0.1:8765;
 * `/status` richiede X-TL4K-Token, mentre `/whoami` è senza auth e restituisce
 * machine_id/hostname/mode/version/current_session_pin. Il server-ui sul proxy
 * (127.0.0.1:8080, mDNS gestioneclasse.local) fa proxy `/api-daemon/*` → 8765.
 * Usiamo `/whoami` (una sola richiesta, senza token) con timeout 1500ms.
 */
import { authService, formatPseudonym } from '@tl4k/frontend-shared';
import type {
  ClientContext,
  ClientContextPseudonym,
  ClientContextUser,
  IssueClientMode,
  TechyClientContext,
} from './types';

// ────────────────────────────────────────────────────────────────
// Helper puri (testabili in ambiente node)
// ────────────────────────────────────────────────────────────────

/**
 * Modalità del client: 'techyweb' se embedded nella shell (`tl4k-embedded=1`)
 * o hostname TechyWeb, altrimenti 'standalone'.
 */
export function detectMode(search: string, hostname: string): IssueClientMode {
  try {
    if (new URLSearchParams(search).get('tl4k-embedded') === '1') return 'techyweb';
  } catch (e) {
    console.warn('[collectClientContext] parse search fallito:', e);
  }
  const h = hostname.toLowerCase();
  if (h.includes('techyweb')) return 'techyweb';
  return 'standalone';
}

/** PIN sessione da query param: app_pin → session_pin → incontro_pin (primo disponibile). */
export function readSessionPinFromQuery(search: string): string | null {
  try {
    const params = new URLSearchParams(search);
    for (const key of ['app_pin', 'session_pin', 'incontro_pin']) {
      const value = params.get(key);
      if (value && value.trim()) return value.trim().toUpperCase();
    }
  } catch (e) {
    console.warn('[collectClientContext] parse query pin fallito:', e);
  }
  return null;
}

/** Parsing basilare OS dallo user agent. */
export function parseOsFromUserAgent(ua: string): string | null {
  if (!ua) return null;
  if (/CrOS/i.test(ua)) return 'ChromeOS';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return null;
}

/** Chiave localStorage del join sessione TechyWeb (SessionManager). */
export const TECHYWEB_SESSION_STORAGE_KEY = 'techyweb_tl4k_session';
/** Chiavi fallback per il PIN sessione corrente. */
export const SESSION_PIN_STORAGE_KEYS = ['tl4k_current_session_pin', 'tl4k-student-pin'];

interface StoredAvatar {
  animal?: string;
  animalColor?: string;
  animal_color?: string;
  shape?: string;
  shapeColor?: string;
  shape_color?: string;
  letter?: string;
}

/**
 * Pseudonimo (dati join sessione) dallo storage. Restituisce null se assente
 * o non parsabile (con warn). `storage` iniettato per i test.
 */
export function readPseudonymFromStorage(
  storage: Pick<Storage, 'getItem'> | undefined,
): ClientContextPseudonym | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(TECHYWEB_SESSION_STORAGE_KEY);
  } catch (e) {
    console.warn('[collectClientContext] storage getItem fallito:', e);
    return null;
  }
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as {
      session?: { pin?: string };
      participant?: { pseudonymIdentityId?: string; avatar?: StoredAvatar };
    };
    const avatar = data.participant?.avatar;
    if (!avatar?.animal) return null;
    const formatted = formatPseudonym({
      animal: avatar.animal,
      animal_color: avatar.animalColor ?? avatar.animal_color ?? '',
      shape: avatar.shape ?? '',
      shape_color: avatar.shapeColor ?? avatar.shape_color ?? '',
      letter: avatar.letter,
    });
    return {
      formatted,
      identity_id: data.participant?.pseudonymIdentityId,
      session_pin: data.session?.pin,
    };
  } catch (e) {
    console.warn('[collectClientContext] sessione stored non parsabile:', e);
    return null;
  }
}

/** PIN sessione dallo storage (fallback quando non presente nella URL). */
export function readSessionPinFromStorage(
  storage: Pick<Storage, 'getItem'> | undefined,
): string | null {
  if (!storage) return null;
  for (const key of SESSION_PIN_STORAGE_KEYS) {
    try {
      const value = storage.getItem(key);
      if (value && value.trim()) return value.trim().toUpperCase();
    } catch (e) {
      console.warn(`[collectClientContext] storage getItem(${key}) fallito:`, e);
    }
  }
  return null;
}

/**
 * URL candidati per lo stato del daemon techy-client, in ordine di preferenza.
 * 1. Daemon locale diretto (student PC kiosk): 127.0.0.1:8765/whoami
 * 2. Server-UI proxy locale: 127.0.0.1:8080/api-daemon/whoami
 * 3. Server-UI proxy via mDNS (gestioneclasse.local:8080)
 */
export const TECHY_CLIENT_STATUS_URLS = [
  'http://127.0.0.1:8765/whoami',
  'http://127.0.0.1:8080/api-daemon/whoami',
  'http://gestioneclasse.local:8080/api-daemon/whoami',
] as const;

/** Timeout default per la chiamata al daemon (spec §5.1). */
export const TECHY_CLIENT_TIMEOUT_MS = 1500;

interface DaemonWhoamiResponse {
  machine_id?: string;
  hostname?: string;
  mode?: string;
  version?: string;
  current_session_pin?: string | null;
}

/**
 * Stato del daemon techy-client: best-effort con timeout globale.
 * Restituisce null (con console.warn) se irraggiungibile — il contesto OS
 * viene omesso ma la segnalazione prosegue.
 */
export async function fetchTechyClientStatus(
  fetchImpl: typeof fetch = typeof fetch !== 'undefined' ? fetch : (async () => {
    throw new Error('fetch non disponibile');
  }) as typeof fetch,
  timeoutMs: number = TECHY_CLIENT_TIMEOUT_MS,
  urls: readonly string[] = TECHY_CLIENT_STATUS_URLS,
): Promise<TechyClientContext | null> {
  const deadline = Date.now() + timeoutMs;
  for (const url of urls) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) break;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), remaining);
    try {
      const res = await fetchImpl(url, { signal: controller.signal });
      if (!res.ok) {
        console.warn(`[collectClientContext] daemon ${url} ha risposto HTTP ${res.status}`);
        continue;
      }
      const data = (await res.json()) as DaemonWhoamiResponse;
      return {
        hostname: data.hostname,
        machine_id: data.machine_id,
        mode: data.mode,
        daemon_version: data.version,
        current_session_pin: data.current_session_pin ?? undefined,
      };
    } catch (e) {
      // Prossimo candidato; il warn finale copre il caso "tutti falliti".
      console.warn(`[collectClientContext] daemon non raggiungibile su ${url}:`, e);
    } finally {
      clearTimeout(timer);
    }
  }
  console.warn('techy-client daemon non raggiungibile: contesto OS omesso');
  return null;
}

// ────────────────────────────────────────────────────────────────
// Collector principale
// ────────────────────────────────────────────────────────────────

export interface CollectClientContextOptions {
  /** Nome app passato dal caller (prop appName). */
  appName: string;
  /** Iniezione per test: implementazione fetch. */
  fetchImpl?: typeof fetch;
  /** Iniezione per test: lettore utente corrente (default authService.getCurrentUser). */
  getCurrentUserImpl?: () => Promise<{ name?: string | null; displayName?: string | null; email?: string | null; role?: string | null } | null>;
  /** Iniezione per test: timeout daemon. */
  daemonTimeoutMs?: number;
}

/**
 * Raccoglie il contesto del client per la segnalazione. Ogni sezione è
 * best-effort: un fallimento non blocca le altre e viene loggato.
 */
export async function collectClientContext(
  options: CollectClientContextOptions,
): Promise<ClientContext> {
  const win = typeof window !== 'undefined' ? window : undefined;
  const location = win?.location;
  const search = location?.search ?? '';
  const hostname = location?.hostname ?? '';
  const href = location?.href ?? '';
  const userAgent = (win && typeof win.navigator !== 'undefined' ? win.navigator.userAgent : '') ?? '';

  // Utente autenticato (alias/email/ruolo) — null se authService non disponibile
  let user: ClientContextUser | null = null;
  try {
    const readUser = options.getCurrentUserImpl ?? (authService.getCurrentUser as () => Promise<{ name?: string | null; displayName?: string | null; email?: string | null; role?: string | null } | null>);
    const current = await readUser();
    if (current) {
      user = {
        alias: (current.displayName ?? current.name) || null,
        email: current.email || null,
        role: current.role || null,
      };
    }
  } catch (e) {
    console.warn('[collectClientContext] lettura utente corrente fallita:', e);
    user = null;
  }

  // Pseudonimo + PIN dal join sessione in storage
  const storage = win?.localStorage;
  const pseudonym = readPseudonymFromStorage(storage);
  const sessionPin =
    readSessionPinFromQuery(search) ??
    pseudonym?.session_pin ??
    readSessionPinFromStorage(storage);

  // Viewport / schermo
  let viewport: string | null = null;
  let screenInfo: string | null = null;
  try {
    if (win && Number.isFinite(win.innerWidth) && Number.isFinite(win.innerHeight)) {
      viewport = `${Math.round(win.innerWidth)}x${Math.round(win.innerHeight)}`;
    }
    if (win?.screen && Number.isFinite(win.screen.width) && Number.isFinite(win.screen.height)) {
      screenInfo = `${Math.round(win.screen.width)}x${Math.round(win.screen.height)}`;
    }
  } catch (e) {
    console.warn('[collectClientContext] lettura viewport/screen fallita:', e);
  }

  // techy-client daemon (timeout 1500ms, fallback null + warn)
  const techyClient = await fetchTechyClientStatus(
    options.fetchImpl,
    options.daemonTimeoutMs,
  );

  return {
    app: options.appName,
    mode: detectMode(search, hostname),
    url: href,
    user_agent: userAgent,
    os: parseOsFromUserAgent(userAgent),
    viewport,
    screen: screenInfo,
    language: win?.navigator?.language ?? null,
    session_pin: sessionPin,
    user,
    pseudonym,
    techy_client: techyClient,
  };
}
